import {
  ArrowIcon,
  Box,
  Button,
  Flex,
  Heading,
  Layout,
  Text,
  TextLink,
} from '@modusign/design-system';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { useBoolean } from 'react-use';
import { PromiseType } from 'utility-types';

import Divider from '@app/components/Divider';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { Modusign36h } from '@app/components/Logos';
import DocumentDownloadErrorModal from '@app/components/Modal/DocumentDownloadErrorModal';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';
import { getDocumentDownload } from '@app/store/transport-layer/document/documentDownloads';

function refreshPage() {
  const { origin } = window.location;
  window.location.href = `${origin}/documents`;
}

function Download({ filepath }: { filepath: string }) {
  return <Box as="iframe" display="none" src={filepath} />;
}

export default function DocumentDownloadPage() {
  const [isRequestDownload, setIsRequestDownload] = useBoolean(false);
  const getDownload = () => {
    Promise.resolve(setIsRequestDownload(false)).then(() => setIsRequestDownload(true));
  };

  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch } = useQuery<
    PromiseType<ReturnType<typeof getDocumentDownload>>,
    Error
  >({
    queryKey: ['documentDownloads', id],
    queryFn: () => getDocumentDownload(id),
    refetchOnWindowFocus: false,
    enabled: !!id,
  });

  return (
    <Box backgroundColor="buttonBackgroundNeutralBase" height="100vh">
      {isLoading && <GlobalLoadingSpinner />}
      <Layout flexDirection="column" margin="auto" maxWidth="md">
        <Box
          backgroundColor="boxBackgroundNeutralLight"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          mb={4}
          p={7}
        >
          <Box onClick={refreshPage}>
            <Modusign36h style={{ cursor: 'pointer', marginBottom: 12 }} />
          </Box>
          <Divider />
          <Flex alignItems="center" flexDirection="column">
            <Heading as="h4" fontSize="h4" mb={7} mt={8}>
              문서 일괄 다운로드
            </Heading>
            <Text as="p" mb={7} textAlign="center">
              다운로드는 네트워크 상황 및 파일 크기에 따라
              <br />
              10분 이상 소요될 수 있습니다.
            </Text>
            <Text as="span" fontSize="sm" fontWeight="medium" mb={7} textAlign="center">
              *모바일 환경에서 올바르게 다운로드 되지
              <br /> 않을 수 있습니다. PC를 이용해주세요.
            </Text>
            {!!data?.urls && (
              <Button mb={6} variant="primary" onClick={getDownload}>
                {data.urls.length}개의 압축파일 다운로드
              </Button>
            )}
            <TextLink mb={1} onClick={refreshPage}>
              문서함으로
              <ArrowIcon direction="right" ml={1} />
            </TextLink>
          </Flex>
        </Box>
      </Layout>
      {error && (
        <DocumentDownloadErrorModal
          errorMessage={coalesceGlobalErrorMessage(error.message)}
          title="완료문서 다운로드 실패"
          onConfirm={() => refetch()}
        />
      )}
      {data && isRequestDownload && data.urls?.map((url) => <Download filepath={url} key={url} />)}
    </Box>
  );
}
